import { useEffect, useRef, useState } from "react";

/**
 * Fades in (and slides up slightly) when the element enters the viewport.
 *
 * Props:
 * - delay: ms to wait before starting animation (used for staggered entrances)
 * - duration: animation duration in ms (default 700)
 * - className/style: forwarded to wrapper
 */
const FadeIn = ({ children, delay = 0, duration = 700, className = "", style, as: Tag = "div" }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const mergedStyle = {
    transitionProperty: "opacity, transform",
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${delay}ms`,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
    ...style,
  };

  return (
    <Tag ref={ref} className={className} style={mergedStyle}>
      {children}
    </Tag>
  );
};

export default FadeIn;
